import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';
import logger from '../utils/logger';
import { API_ENDPOINTS } from '../utils/apiConfig';

export const useCollaboration = ({ userId, userName, userRole, token }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const [modalLocks, setModalLocks] = useState(new Map());
  const [collaborationMessages, setCollaborationMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState(new Map());
  const [connectionError, setConnectionError] = useState(null);
  
  const lockExtensionTimers = useRef(new Map());
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 3; // Réduit pour éviter les boucles
  const authenticationTimeout = useRef(null);
  const connectionInProgress = useRef(false); // ✅ NOUVEAU: Éviter connexions multiples

  // ✅ NOUVEAU: Stabiliser le token avec useMemo
  const stableToken = useMemo(() => token, [token]);

  // ✅ NOUVEAU: Vérification JWT stable avec useCallback
  const isValidJWT = useCallback((tokenToCheck) => {
    if (!tokenToCheck) {
      logger.error('❌ Token manquant pour WebSocket');
      return false;
    }
    
    try {
      // Vérifier la structure JWT (3 parties séparées par des points)
      const parts = tokenToCheck.split('.');
      if (parts.length !== 3) {
        logger.error('❌ Token JWT malformé (pas 3 parties)');
        return false;
      }
      
      // Décoder le payload pour vérifier l'expiration (avec support UTF-8 pour les accents)
      const binaryString = atob(parts[1]);
      const bytes = Uint8Array.from(binaryString, c => c.charCodeAt(0));
      const payload = JSON.parse(new TextDecoder().decode(bytes));
      const now = Date.now() / 1000;
      
      if (payload.exp && payload.exp < now) {
        logger.error('❌ Token JWT expiré pour WebSocket:', {
          exp: new Date(payload.exp * 1000).toLocaleString(),
          expISO: new Date(payload.exp * 1000).toISOString(),
          expTimestamp: payload.exp,
          now: new Date().toLocaleString(),
          nowISO: new Date().toISOString(),
          nowTimestamp: now,
          diffSeconds: Math.round(now - payload.exp)
        });
        return false;
      }
      
      // ✅ CORRECTION: Adaptation à la structure réelle du token
      const userName = payload.first_name && payload.last_name 
        ? `${payload.first_name} ${payload.last_name}`
        : payload.name || payload.email || 'Utilisateur';
      
      logger.debug('✅ Token JWT valide pour WebSocket:', {
        userId: payload.userId || payload.id || payload.sub,
        user: userName,
        email: payload.email,
        role: payload.role,
        exp: payload.exp ? new Date(payload.exp * 1000).toLocaleString() : 'Pas d\'expiration',
        iat: payload.iat ? new Date(payload.iat * 1000).toLocaleString() : 'Pas de date création',
        fullPayload: payload // ✅ DEBUG: Voir toute la structure
      });
      
      return true;
    } catch (error) {
      logger.error('❌ Erreur validation JWT:', error);
      return false;
    }
  }, []); // ✅ Pas de dépendances - fonction stable

  // Initialisation de la connexion Socket.io
  useEffect(() => {
    // ✅ Éviter connexions multiples
    if (connectionInProgress.current) {
      logger.debug('🔌 Connexion déjà en cours, abandon');
      return;
    }

    // ✅ Éviter la boucle infinie - vérifier si on a déjà un socket connecté
    if (socket && isConnected && isAuthenticated) {
      logger.debug('🔌 Socket déjà connecté et authentifié, pas de reconnexion');
      return;
    }

    // ✅ Vérifier le token avant connexion
    if (!isValidJWT(stableToken)) {
      setConnectionError('Token JWT invalide ou expiré');
      return;
    }

    logger.debug('🔌 Tentative connexion WebSocket...');
    connectionInProgress.current = true;

    const newSocket = io(API_ENDPOINTS.WEBSOCKET_URL, {
      auth: {
        token: stableToken
      },
      transports: ['websocket', 'polling'],
      timeout: 10000, // 10 secondes de timeout
      // ✅ SUPPRIMÉ: forceNew: true (causait la boucle infinie)
    });

    // Rafraîchir le token depuis localStorage avant chaque reconnexion
    newSocket.io.on('reconnect_attempt', () => {
      const freshToken = localStorage.getItem('token');
      if (freshToken) {
        newSocket.auth.token = freshToken;
        logger.debug('🔄 Token WebSocket rafraîchi depuis localStorage avant reconnexion');
      }
    });

    setSocket(newSocket);

    // Gestion des événements de connexion
    newSocket.on('connect', () => {
      logger.debug('🔌 Socket.io connecté - ID:', newSocket.id);
      setIsConnected(true);
      setConnectionError(null);
      reconnectAttempts.current = 0;
      
      // ✅ Authentification avec timeout (token frais depuis localStorage)
      logger.debug('🔐 Envoi authentification...');
      newSocket.emit('authenticate', {
        userId,
        userName,
        userRole,
        token: localStorage.getItem('token') || stableToken
      });
      
      // Timeout d'authentification
      authenticationTimeout.current = setTimeout(() => {
        if (!isAuthenticated) {
          logger.error('⏰ Timeout authentification WebSocket');
          setConnectionError('Timeout authentification WebSocket');
          connectionInProgress.current = false; // ✅ Libérer le verrou
        }
      }, 5000);
    });

    newSocket.on('disconnect', (reason) => {
      logger.warn('🔌❌ Déconnexion WebSocket:', reason);
      setIsConnected(false);
      setIsAuthenticated(false);
      connectionInProgress.current = false; // ✅ Libérer le verrou
      
      if (authenticationTimeout.current) {
        clearTimeout(authenticationTimeout.current);
      }
    });

    newSocket.on('connect_error', (error) => {
      logger.error('❌ Erreur connexion WebSocket:', error.message);
      setConnectionError(`Erreur connexion: ${error.message}`);
      connectionInProgress.current = false; // ✅ Libérer le verrou
      
      if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current++;
        logger.debug(`🔄 Tentative de reconnexion (${reconnectAttempts.current}/${maxReconnectAttempts})`);
        // ✅ Pas de reconnexion automatique immédiate
        setTimeout(() => {
          connectionInProgress.current = false;
        }, 2000);
      } else {
        logger.error('❌ Abandon reconnexion WebSocket');
        setConnectionError('Impossible de se connecter au serveur temps réel');
      }
    });

    // ✅ Événements d'authentification améliorés
    newSocket.on('authenticated', (data) => {
      logger.debug('✅ Authentification WebSocket réussie:', data);
      setIsAuthenticated(true);
      setConnectionError(null);
      connectionInProgress.current = false; // ✅ Connexion terminée avec succès
      
      if (authenticationTimeout.current) {
        clearTimeout(authenticationTimeout.current);
      }
    });

    newSocket.on('authentication-failed', (data) => {
      logger.error('❌ Authentification WebSocket échouée:', data);
      setIsAuthenticated(false);
      setConnectionError(`Authentification échouée: ${data.error}`);
      connectionInProgress.current = false; // ✅ Libérer le verrou
      
      if (authenticationTimeout.current) {
        clearTimeout(authenticationTimeout.current);
      }
    });

    // ✅ NOUVEAU: Gestion re-authentification requise
    newSocket.on('authentication-required', (data) => {
      logger.warn('🔄 Ré-authentification WebSocket requise:', data);
      setIsAuthenticated(false);
      
      // ✅ Éviter la boucle de ré-authentification (token frais depuis localStorage)
      const currentToken = localStorage.getItem('token') || stableToken;
      if (!connectionInProgress.current && isValidJWT(currentToken)) {
        logger.debug('🔄 Tentative re-authentification...');
        newSocket.emit('authenticate', {
          userId,
          userName,
          userRole,
          token: currentToken
        });
      } else {
        setConnectionError('Token expiré - ré-authentification impossible');
        connectionInProgress.current = false;
      }
    });

    // Gestion des utilisateurs actifs
    newSocket.on('active-users', (users) => {
      logger.debug('📥 Liste utilisateurs reçue:', users?.length || 0, users);
      setActiveUsers(users || []);
    });

    newSocket.on('user-connected', (data) => {
      logger.debug('👋 Utilisateur connecté:', data.user?.userName);
      if (data.user) {
        setActiveUsers(prev => {
          // ✅ Éviter les doublons basés sur socketId
          const filtered = prev.filter(u => u.socketId !== data.user.socketId);
          const newList = [...filtered, data.user];
          logger.debug('📊 Utilisateurs après connexion:', newList.length);
          return newList;
        });
      }
      console.info(data.message);
    });

    newSocket.on('user-disconnected', (data) => {
      logger.debug('👋 Utilisateur déconnecté:', data.user?.userName, data.user?.socketId);
      if (data.user) {
        setActiveUsers(prev => {
          const newList = prev.filter(user => user.socketId !== data.user.socketId);
          logger.debug('📊 Utilisateurs après déconnexion:', newList.length);
          return newList;
        });
      }
      console.info(data.message);
    });

    // Gestion des verrous de modals
    newSocket.on('modal-lock-granted', (data) => {
      logger.debug('🎉 VERROU REÇU:', data);
      setModalLocks(prev => {
        const newMap = new Map(prev);
        newMap.set(data.lockKey, data);
        logger.debug('🔒 Verrous actifs:', newMap.size);
        return newMap;
      });
      setupLockExtension(data.lockKey, data.expiresAt);
    });

    newSocket.on('modal-lock-denied', (data) => {
      logger.debug('❌ VERROU REFUSÉ:', data);
    });

    newSocket.on('modal-lock-released', (data) => {
      setModalLocks(prev => {
        const newMap = new Map(prev);
        newMap.delete(data.lockKey);
        logger.debug('🔓 Verrous actifs après libération:', newMap.size);
        return newMap;
      });
      clearLockExtension(data.lockKey);
    });

    newSocket.on('modal-lock-expired', (data) => {
      setModalLocks(prev => {
        const newMap = new Map(prev);
        newMap.delete(data.lockKey);
        return newMap;
      });
      clearLockExtension(data.lockKey);
      logger.warn('⏰ Votre verrou a expiré:', data.lockKey);
    });

    // Notifications de verrous
    newSocket.on('modal-locked-notification', (data) => {
      console.info('🔒', data.message);
    });

    newSocket.on('modal-unlocked-notification', (data) => {
      console.info('🔓', data.message);
    });

    newSocket.on('lock-conflict-notification', (data) => {
      logger.warn('⚠️', data.message);
    });

    // Messages de collaboration
    newSocket.on('collaboration-message', (message) => {
      setCollaborationMessages(prev => [...prev, message]);
      console.info(`💬 ${message.from?.userName}: ${message.message}`);
    });

    // Notifications de frappe
    newSocket.on('user-typing-notification', (data) => {
      setTypingUsers(prev => new Map(prev).set(`${data.modalType}-${data.modalId}`, data.user));
    });

    newSocket.on('user-stopped-typing', (data) => {
      setTypingUsers(prev => {
        const newMap = new Map(prev);
        newMap.delete(`${data.modalType}-${data.modalId}`);
        return newMap;
      });
    });

    // ✅ NOUVEAU: Debug ping/pong
    newSocket.on('ping', () => {
      newSocket.emit('pong');
    });

    newSocket.on('pong', () => {
      logger.debug('💓 Pong reçu');
    });

    return () => {
      // ✅ Nettoyage sécurisé - éviter les reconnexions
      logger.debug('🧹 Nettoyage connexion WebSocket');
      connectionInProgress.current = false; // ✅ Libérer le verrou
      
      if (authenticationTimeout.current) {
        clearTimeout(authenticationTimeout.current);
        authenticationTimeout.current = null;
      }
      
      lockExtensionTimers.current.forEach(timer => clearTimeout(timer));
      lockExtensionTimers.current.clear();
      
      // ✅ Déconnexion propre sans forcer de reconnexion
      if (newSocket && newSocket.connected) {
        newSocket.removeAllListeners(); // ✅ NOUVEAU: Éviter les listeners orphelins
        newSocket.disconnect();
      }
    };
  }, [stableToken, userId, userName, userRole]); // ✅ CORRECTION: Dépendances stables

  // ✅ NOUVEAU: Éviter les re-renders inutiles
  useEffect(() => {
    // Éviter de redéclencher si déjà connecté et authentifié
    if (socket && isConnected && isAuthenticated) {
      return;
    }
  }, [socket, isConnected, isAuthenticated]);

  // Configuration de l'extension automatique des verrous
  const setupLockExtension = useCallback((lockKey, expiresAt) => {
    const timeUntilExpiry = new Date(expiresAt).getTime() - Date.now();
    const extensionTime = Math.max(timeUntilExpiry - 5 * 60 * 1000, 0); // 5 minutes avant expiration

    if (extensionTime > 0) {
      const timer = setTimeout(() => {
        if (socket && isConnected && isAuthenticated) {
          const [modalType, modalId] = lockKey.split('-');
          socket.emit('extend-modal-lock', { modalType, modalId });
          logger.debug('🔄 Extension verrou demandée:', lockKey);
        }
      }, extensionTime);

      lockExtensionTimers.current.set(lockKey, timer);
    }
  }, [socket, isConnected, isAuthenticated]);

  const clearLockExtension = useCallback((lockKey) => {
    const timer = lockExtensionTimers.current.get(lockKey);
    if (timer) {
      clearTimeout(timer);
      lockExtensionTimers.current.delete(lockKey);
    }
  }, []);

  // ✅ Fonctions utilitaires optimisées avec useCallback stable
  const requestModalLock = useCallback((modalType, modalId) => {
    if (!socket || !isConnected) {
      logger.warn('❌ Socket non connecté pour demande verrou');
      return Promise.resolve(false);
    }
    
    if (!isAuthenticated) {
      logger.warn('❌ Socket non authentifié pour demande verrou');
      return Promise.resolve(false);
    }
    
    logger.debug(`🔒 Demande verrou: ${modalType}-${modalId}`);
    socket.emit('request-modal-lock', { modalType, modalId });
    return Promise.resolve(true);
  }, [socket, isConnected, isAuthenticated]);

  const releaseModalLock = useCallback((modalType, modalId) => {
    if (!socket || !isConnected) {
      logger.warn('❌ Socket non connecté pour libération verrou');
      return;
    }
    
    if (!isAuthenticated) {
      logger.warn('❌ Socket non authentifié pour libération verrou');
      return;
    }
    
    logger.debug(`🔓 Libération verrou: ${modalType}-${modalId}`);
    socket.emit('release-modal-lock', { modalType, modalId });
  }, [socket, isConnected, isAuthenticated]);

  const sendCollaborationMessage = useCallback((message, type = 'info', targetUsers) => {
    if (!socket || !isConnected || !isAuthenticated) {
      logger.warn('❌ Socket non prêt pour message collaboration');
      return;
    }
    
    logger.debug(`💬 Envoi message: ${message}`);
    socket.emit('send-collaboration-message', { message, type, targetUsers });
  }, [socket, isConnected, isAuthenticated]);

  const notifyUserTyping = useCallback((modalType, modalId, field) => {
    if (!socket || !isConnected || !isAuthenticated) {
      return;
    }
    
    socket.emit('user-typing', { modalType, modalId, field });
  }, [socket, isConnected, isAuthenticated]);

  const hasModalLock = useCallback((modalType, modalId) => {
    const lockKey = `${modalType}-${modalId}`;
    const hasLock = modalLocks.has(lockKey);
    return hasLock;
  }, [modalLocks]);

  const getTypingUsers = useCallback((modalType, modalId) => {
    const key = `${modalType}-${modalId}`;
    return typingUsers.get(key);
  }, [typingUsers]);

  // ✅ NOUVEAU: Fonction de diagnostic
  const getConnectionStatus = useCallback(() => {
    return {
      isConnected,
      isAuthenticated,
      activeUsers: activeUsers.length,
      modalLocks: modalLocks.size,
      connectionError,
      socketId: socket?.id
    };
  }, [isConnected, isAuthenticated, activeUsers.length, modalLocks.size, connectionError, socket?.id]);

  // ✅ SUPPRIMÉ: Debug périodique qui causait des re-renders

  return {
    socket, // Phase 2 - Exposé pour usePresenceTracking
    isConnected,
    isAuthenticated,
    activeUsers,
    modalLocks,
    collaborationMessages,
    connectionError,
    requestModalLock,
    releaseModalLock,
    sendCollaborationMessage,
    notifyUserTyping,
    hasModalLock,
    getTypingUsers,
    getConnectionStatus // ✅ Pour debug si nécessaire
  };
};